/**
 * Lifestyle-inflation helper — web mirror of
 * `shared/src/util/lifestyle-inflation.ts`. Web does NOT import
 * @hearth/shared (5.1 lock); keep the two files in sync.
 *
 * Phase 13 (2026-05-09). See the shared file's top-of-file comment for
 * citations + design rationale.
 */

export interface CategoryYearlySpend {
  readonly category_label: string;
  readonly current_ttm_cents: number;
  readonly prior_ttm_cents: number;
}

export interface LifestyleInflationInput {
  readonly by_category: readonly CategoryYearlySpend[];
  readonly current_ttm_income_cents: number;
  readonly prior_ttm_income_cents: number;
  readonly warn_pp?: number;
  readonly min_current_cents?: number;
}

export interface LifestyleInflationFlag {
  readonly category_label: string;
  readonly current_ttm_cents: number;
  readonly prior_ttm_cents: number;
  readonly growth_pct: number;
  readonly flagged: boolean;
}

export interface LifestyleInflationResult {
  readonly income_growth_pct: number;
  readonly threshold_pct: number;
  readonly rows: readonly LifestyleInflationFlag[];
  readonly flagged_count: number;
  readonly creep_above_threshold_cents: number;
}

const DEFAULT_WARN_PP = 5;
const DEFAULT_MIN_CURRENT_CENTS = 5_000;

export function evaluateLifestyleInflation(
  input: LifestyleInflationInput,
): LifestyleInflationResult {
  const warnPp = input.warn_pp ?? DEFAULT_WARN_PP;
  const minCurrent = input.min_current_cents ?? DEFAULT_MIN_CURRENT_CENTS;

  const incomeGrowthPct =
    input.prior_ttm_income_cents > 0
      ? ((input.current_ttm_income_cents - input.prior_ttm_income_cents) /
          input.prior_ttm_income_cents) *
        100
      : 0;
  const thresholdPct = incomeGrowthPct + warnPp;

  const rows: LifestyleInflationFlag[] = [];
  let creepAboveThreshold = 0;
  for (const c of input.by_category) {
    const growthPct =
      c.prior_ttm_cents > 0
        ? ((c.current_ttm_cents - c.prior_ttm_cents) / c.prior_ttm_cents) *
          100
        : c.current_ttm_cents > 0
          ? Number.POSITIVE_INFINITY
          : 0;
    const flagged =
      c.prior_ttm_cents > 0 &&
      c.current_ttm_cents >= minCurrent &&
      growthPct > thresholdPct;
    rows.push({
      category_label: c.category_label,
      current_ttm_cents: c.current_ttm_cents,
      prior_ttm_cents: c.prior_ttm_cents,
      growth_pct: growthPct,
      flagged,
    });
    if (flagged) {
      const allowedCurrent = Math.round(
        c.prior_ttm_cents * (1 + thresholdPct / 100),
      );
      const above = c.current_ttm_cents - allowedCurrent;
      if (above > 0) creepAboveThreshold += above;
    }
  }
  rows.sort((a, b) => b.growth_pct - a.growth_pct);
  return {
    income_growth_pct: incomeGrowthPct,
    threshold_pct: thresholdPct,
    rows,
    flagged_count: rows.filter((r) => r.flagged).length,
    creep_above_threshold_cents: creepAboveThreshold,
  };
}
